import { createSlice } from "@reduxjs/toolkit";

/**
 * Safely load persisted configs from sessionStorage.
 * Used only for initial state hydration.
 */
const loadPersistedConfigs = () => {
	try {
		return JSON.parse(sessionStorage.getItem("configs") || "{}");
	} catch {
		return {};
	}
};

/**
 * Persist configs to sessionStorage.
 * Redux is the source of truth; this is persistence only.
 */
const persist = (state) => {
	try {
		sessionStorage.setItem(
			"configs",
			JSON.stringify({
				lod: state.lod,
				showConsole: state.showConsole,
				showTesting: state.showTesting,
				showTimeoutCounter: state.showTimeoutCounter,
				showTimeoutShortWindow: state.showTimeoutShortWindow,
			})
		);
	} catch {
		// ignore storage errors (private mode, quota, etc.)
	}
};

const persisted = loadPersistedConfigs();

const initialState = {
	// Debug / logging
	lod: typeof persisted.lod === "number" ? persisted.lod : 1,

	// Debug toggles
	// showConsole: !!persisted.showConsole,
	showConsole: typeof persisted.showConsole === "boolean" ? persisted.showConsole : true,

	showTesting: !!persisted.showTesting,

	// Dedicated timeout diagnostics toggles (independent from LOD/showTesting).
	showTimeoutCounter: !!persisted.showTimeoutCounter,
	showTimeoutShortWindow: !!persisted.showTimeoutShortWindow,
};

const configSlice = createSlice({
	name: "configs",
	initialState,
	reducers: {
		/* -----------------------------
		 * Individual setters
		 * ----------------------------- */
		// persistence by reducers is intentional, user-driven preference saving
		setLod(state, action) {
			state.lod = action.payload;
			persist(state);
		},
		setShowConsole(state, action) {
			state.showConsole = !!action.payload;
			persist(state);
		},
		setShowTesting(state, action) {
			state.showTesting = !!action.payload;
			persist(state);
		},
		// Controls timeout counter log visibility only.
		setShowTimeoutCounter(state, action) {
			state.showTimeoutCounter = !!action.payload;
			persist(state);
		},
		// Controls short warning/logout windows only.
		setShowTimeoutShortWindow(state, action) {
			state.showTimeoutShortWindow = !!action.payload;
			persist(state);
		},

		/* -----------------------------
		 * Hydration (login / reload)
		 * ----------------------------- */
		hydrateConfigs(state, action) {
			const incoming = action.payload || {};

			if (typeof incoming.lod === "number") state.lod = incoming.lod;
			if (typeof incoming.showConsole === "boolean") state.showConsole = incoming.showConsole;
			if (typeof incoming.showTesting === "boolean") state.showTesting = incoming.showTesting;
			if (typeof incoming.showTimeoutCounter === "boolean") {
				state.showTimeoutCounter = incoming.showTimeoutCounter;
			}
			if (typeof incoming.showTimeoutShortWindow === "boolean") {
				state.showTimeoutShortWindow = incoming.showTimeoutShortWindow;
			}

			persist(state);
		},

		/* -----------------------------
		 * Reset (logout boundary)
		 * NOTE: Defaults here represent a safe post-logout state,
		 * not initial app startup defaults.
		 * ----------------------------- */
		resetConfigs() {
			const defaults = {
				lod: 1,
				showConsole: false,
				showTesting: false,
				showTimeoutCounter: false,
				showTimeoutShortWindow: false,
			};

			try {
				sessionStorage.setItem("configs", JSON.stringify(defaults));
			} catch {
				// ignore
			}

			return defaults;
		},
	},
});

export const {
	setLod,
	setShowConsole,
	setShowTesting,
	setShowTimeoutCounter,
	setShowTimeoutShortWindow,
	hydrateConfigs,
	resetConfigs,
} =
	configSlice.actions;

export default configSlice.reducer;
